import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  Receipt,
  Calendar,
  User,
  Maximize2,
  Minimize2,
  FileText,
} from 'lucide-react';
import type { Expense } from '@/types';
import { format } from 'date-fns';
import { ApprovalHistoryTimeline } from '../time/approval-history-timeline';

interface ExpenseEntriesTabProps {
  projectId: number;
  expenses: Expense[];
  employees: Array<{ id: number; name: string }>;
}

type SortField = 'date' | 'employeeId' | 'amount' | 'category' | 'description';
type SortDirection = 'asc' | 'desc';
type ApprovalFilter = 'all' | 'approved' | 'pending';
type BillableFilter = 'all' | 'billable' | 'non-billable';

interface ExpenseSummary {
  totalAmount: number;
  billableAmount: number;
  nonBillableAmount: number;
  approvedAmount: number;
  pendingAmount: number;
  reimbursableAmount: number;
  uniqueEmployees: number;
  expensesByCategory: Record<string, number>;
}

export function ExpenseEntriesTab({
  projectId,
  expenses,
  employees,
}: ExpenseEntriesTabProps) {
  const { currentCompany } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
  const [billableFilter, setBillableFilter] = useState<BillableFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryExpense, setApprovalHistoryExpense] = useState<Expense | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);

  const getEmployeeName = (expense: Expense): string => {
    if (expense.employeeName) return expense.employeeName;
    const employee = employees.find((e) => e.id === expense.employeeId);
    return employee?.name || 'Unknown Employee';
  };

  // Get unique employees for filter
  const uniqueEmployees = useMemo(() => {
    const employeeIds = Array.from(new Set(expenses.map((e) => e.employeeId)));
    return employeeIds
      .map((id) => {
        const expense = expenses.find((e) => e.employeeId === id);
        return {
          id,
          name: expense ? getEmployeeName(expense) : 'Unknown Employee',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [expenses, employees]);

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    const categories = Array.from(new Set(expenses.map((e) => e.category)));
    return categories.sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  // Calculate summary
  const summary: ExpenseSummary = useMemo(() => {
    const totalAmount = expenses.reduce((sum, e) => sum + e.amount, 0);
    const billableAmount = expenses.filter((e) => e.billable).reduce((sum, e) => sum + e.amount, 0);
    const nonBillableAmount = totalAmount - billableAmount;
    const approvedAmount = expenses.filter((e) => e.approved).reduce((sum, e) => sum + e.amount, 0);
    const pendingAmount = expenses.filter((e) => !e.approved).reduce((sum, e) => sum + e.amount, 0);
    const reimbursableAmount = expenses.filter((e) => e.reimbursable).reduce((sum, e) => sum + e.amount, 0);
    const uniqueEmployees = new Set(expenses.map((e) => e.employeeId)).size;

    const expensesByCategory: Record<string, number> = {};
    expenses.forEach((e) => {
      expensesByCategory[e.category] = (expensesByCategory[e.category] || 0) + e.amount;
    });

    return {
      totalAmount,
      billableAmount,
      nonBillableAmount,
      approvedAmount,
      pendingAmount,
      reimbursableAmount,
      uniqueEmployees,
      expensesByCategory,
    };
  }, [expenses]);

  // Filter and sort data
  const filteredAndSortedExpenses = useMemo(() => {
    let filtered = [...expenses];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.description.toLowerCase().includes(query) ||
          e.category.toLowerCase().includes(query) ||
          getEmployeeName(e).toLowerCase().includes(query) ||
          e.date.includes(query) ||
          (e.merchantName && e.merchantName.toLowerCase().includes(query))
      );
    }

    // Apply employee filter
    if (employeeFilter !== 'all') {
      filtered = filtered.filter((e) => e.employeeId === Number(employeeFilter));
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((e) => e.category === categoryFilter);
    }

    // Apply approval filter
    if (approvalFilter === 'approved') {
      filtered = filtered.filter((e) => e.approved);
    } else if (approvalFilter === 'pending') {
      filtered = filtered.filter((e) => !e.approved);
    }

    // Apply billable filter
    if (billableFilter === 'billable') {
      filtered = filtered.filter((e) => e.billable);
    } else if (billableFilter === 'non-billable') {
      filtered = filtered.filter((e) => !e.billable);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      if (sortField === 'date') {
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
      } else if (sortField === 'employeeId') {
        aValue = getEmployeeName(a);
        bValue = getEmployeeName(b);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return filtered;
  }, [expenses, searchQuery, employeeFilter, categoryFilter, approvalFilter, billableFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedExpenses.length / pageSize);
  const paginatedExpenses = filteredAndSortedExpenses.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, employeeFilter, categoryFilter, approvalFilter, billableFilter, sortField, sortDirection, pageSize]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 inline" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4 inline" />
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  // Empty state
  if (expenses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Expenses</h3>
                <p className="text-sm text-muted-foreground">
                  There are no expenses associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Billable Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.billableAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((summary.billableAmount / summary.totalAmount) * 100).toFixed(0)}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Approved Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <div className="text-2xl font-bold">{formatCurrency(summary.approvedAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {((summary.approvedAmount / summary.totalAmount) * 100).toFixed(0)}% approved
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{summary.uniqueEmployees}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Submitting expenses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Expense Entries</CardTitle>
          <CardDescription>
            Expenses submitted for this project{currentCompany ? ` from ${currentCompany.name}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search expenses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {uniqueEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={String(employee.id)}>
                      {employee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {uniqueCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={approvalFilter} onValueChange={(value) => setApprovalFilter(value as ApprovalFilter)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by approval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              <Select value={billableFilter} onValueChange={(value) => setBillableFilter(value as BillableFilter)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by billable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="billable">Billable</SelectItem>
                  <SelectItem value="non-billable">Non-Billable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground mb-4">
            Showing {paginatedExpenses.length} of {filteredAndSortedExpenses.length} expenses
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('date')}>
                    Date {getSortIcon('date')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('employeeId')}>
                    Employee {getSortIcon('employeeId')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('category')}>
                    Category {getSortIcon('category')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort('description')}>
                    Description {getSortIcon('description')}
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => handleSort('amount')}>
                    Amount {getSortIcon('amount')}
                  </TableHead>
                  <TableHead>Billable</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No expenses match your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedExpenses.map((expense) => (
                    <TableRow key={expense.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <button
                          onClick={() => setSelectedExpense(expense)}
                          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                          {format(new Date(expense.date), 'dd MMM yyyy')}
                        </button>
                      </TableCell>
                      <TableCell>{getEmployeeName(expense)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{expense.category}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">{expense.description}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(expense.amount)}</TableCell>
                      <TableCell>
                        {expense.billable ? (
                          <Badge variant="default" accent="info" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Billable
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Non-Billable
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {expense.approved ? (
                          <Badge 
                            variant="success" 
                            accent="success" 
                            className="gap-1 cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApprovalHistoryExpense(expense);
                              setApprovalHistorySheetOpen(true);
                            }}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Approved
                          </Badge>
                        ) : (
                          <Badge 
                            variant="warning" 
                            accent="warning" 
                            className="gap-1 cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApprovalHistoryExpense(expense);
                              setApprovalHistorySheetOpen(true);
                            }}
                          >
                            <Clock className="h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(expense.receiptUrl || (expense.attachments && expense.attachments.length > 0)) ? (
                          <Badge variant="outline" className="gap-1">
                            <FileText className="h-3 w-3" />
                            Attached
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filteredAndSortedExpenses.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet
        open={!!selectedExpense}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedExpense(null);
          }
        }}
      >
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedExpense && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      <SheetTitle>Expense Details</SheetTitle>
                    </div>
                    <SheetDescription className="space-y-1">
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(selectedExpense.date), 'EEEE, dd MMMM yyyy')}
                      </div>
                      <div className="text-xs">
                        Internal ID: <span className="font-mono font-medium">{selectedExpense.id}</span>
                      </div>
                      {selectedExpense.externalExpenseId && (
                        <div className="text-xs">
                          Expense ID: <span className="font-mono font-medium">{selectedExpense.externalExpenseId}</span>
                        </div>
                      )}
                    </SheetDescription>
                  </div>
                  {selectedExpense.externalExpenseId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="View in expense system"
                      onClick={() => {
                        // This would link to the external expense system
                        // e.g., window.open(`https://expense-system.com/expenses/${selectedExpense.externalExpenseId}`, '_blank')
                      }}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Status Badges */}
                <div className="flex gap-2 flex-wrap">
                  {selectedExpense.billable ? (
                    <Badge variant="default" accent="info" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Billable
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <XCircle className="h-3 w-3" />
                      Non-Billable
                    </Badge>
                  )}
                  {selectedExpense.approved ? (
                    <Badge variant="success" accent="success" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Approved
                    </Badge>
                  ) : (
                    <Badge variant="warning" accent="warning" className="gap-1">
                      <Clock className="h-3 w-3" />
                      Pending Approval
                    </Badge>
                  )}
                  {selectedExpense.reimbursable && (
                    <Badge variant="outline" className="gap-1">
                      <DollarSign className="h-3 w-3" />
                      Reimbursable
                    </Badge>
                  )}
                  {selectedExpense.policyViolation && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Policy Violation
                    </Badge>
                  )}
                </div>

                {/* Key Information */}
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Employee</label>
                    <p className="mt-1 text-sm font-medium">{getEmployeeName(selectedExpense)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="mt-1 text-sm">{selectedExpense.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Amount</label>
                      <p className="mt-1 text-lg font-semibold">{formatCurrency(selectedExpense.amount)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Date</label>
                      <p className="mt-1 text-sm">{format(new Date(selectedExpense.date), 'dd MMM yyyy')}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Category</label>
                      <p className="mt-1">
                        <Badge variant="outline">{selectedExpense.category}</Badge>
                      </p>
                    </div>
                    {selectedExpense.merchantName && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Merchant</label>
                        <p className="mt-1 text-sm">{selectedExpense.merchantName}</p>
                      </div>
                    )}
                  </div>

                  {selectedExpense.policyViolation && selectedExpense.policyViolationReason && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <label className="text-sm font-medium text-destructive">Policy Violation</label>
                      <p className="mt-1 text-sm text-destructive">{selectedExpense.policyViolationReason}</p>
                    </div>
                  )}

                  {selectedExpense.reviewerComment && (
                    <div className="p-3 bg-muted rounded-lg">
                      <label className="text-sm font-medium text-muted-foreground">Reviewer Comment</label>
                      <p className="mt-1 text-sm">{selectedExpense.reviewerComment}</p>
                    </div>
                  )}

                  {(selectedExpense.receiptUrl || (selectedExpense.attachments && selectedExpense.attachments.length > 0)) && (
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-medium mb-3">Attachments</h4>
                      <div className="space-y-2">
                        {selectedExpense.receiptUrl && (
                          <Button variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={selectedExpense.receiptUrl} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4 mr-2" />
                              View Receipt
                              <ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        )}
                        {selectedExpense.attachments?.map((attachment) => (
                          <Button key={attachment.id} variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4 mr-2" />
                              {attachment.fileName}
                              <ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t">
                  {selectedExpense.externalUrl && (
                  <Button className="w-full" variant="outline" asChild>
                    <a
                      href={selectedExpense.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View in Expense System
                    </a>
                  </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Approval History Sheet */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>
                    Approval History for Expense
                  </DialogTitle>
                  <DialogDescription>
                    {approvalHistoryExpense && (
                      <>
                        {format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}
                      </>
                    )}
                  </DialogDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(false)}
                  title="Return to sheet view"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryExpense.approvalHistory}
                  timeEntryId={String(approvalHistoryExpense.id)}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>
                    Approval History for Expense
                  </SheetTitle>
                  <SheetDescription>
                    {approvalHistoryExpense && (
                      <>
                        {format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}
                      </>
                    )}
                  </SheetDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setApprovalHistoryExpanded(true)}
                  title="Expand to full view"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline 
                  approvalHistory={approvalHistoryExpense.approvalHistory}
                  timeEntryId={String(approvalHistoryExpense.id)}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
